/* ============
 * Bills Index
 * ============
 *
 * This page shows the list of Bills.
 */
import moment from 'moment';
import { mapGetters, mapMutations } from 'vuex';
import billService from './../../../../services/bill';
import invoiceService from './../../../../services/invoice';
import accountService from './../../../../services/account';
import debounce from 'debounce';
import SearchMixin from './../../../../mixins/search';
import store from './../../../../store';
import bulkProcessBillModal from './../../../../components/bulkProcessBillModal/bulkProcessBillModal.vue';

/**
 * True when due date has passed and the bill should be treated as unpaid/overdue in the UI.
 * Matches date logic used for payment reminders (see bill edit).
 */
function billIsOverdueForDisplay(b) {
  if (!b.dueDate || !moment(b.dueDate).isValid()) return false;
  if (b.billType === 'Credit Note') return false;
  const blocked = ['Cleared', 'Cancelled', 'Draft', 'Pending'];
  if (blocked.includes(b.billStatus)) return false;
  return moment().isAfter(moment(b.dueDate).startOf('day'));
}

export default {
  data() {
    return {
      loading: true,
      sortValue: '',
      info: 'info',
      primary: 'primary',
      search: '',
      limit: 25,
      multipleSelection: [],
      bulkSelectionLimit: 25,
      showAction: false,
      action: null,
    };
  },

  computed: {
    ...mapGetters({
      bills: 'paginatedBills',
      view: 'getView',
      searchQuery: 'billQuery',
    }),

    billsListView() {
      return this.bills.items.map((c) => {
        const amountDue = typeof c.amountDue !== 'undefined' ? `${new Intl.NumberFormat('no-NO', { style: 'currency', currency: 'NOK' }).format(c.amountDue)}` : '';
        const overdue = billIsOverdueForDisplay(c);
        return {
          name: `#${c.billingNumber}`,
          type: (this.$t(`static.bills.edit.${c.billType.replace(/\s+/g, '').toLowerCase()}`)),
          status: (this.$t(`static.reports.bills.${c.billStatus.replace(/\s+/g, '').toLowerCase()}`)),
          client: c.client,
          billType: c.billType,
          totalValue: typeof c.totalValue !== 'undefined' ? `${c.totalValue.toFixed(2)} NOK` : '',
          totalQuantity: typeof c.totalValue !== 'undefined' ? `${c.totalQuantity} ${this.$t('static.orders.index.qty')}` : '',
          id: c.id,
          billProducts: c.billProducts,
          hashid: c.hashid,
          paymentDate: c.paymentDate || moment.now(),
          dueDate: moment(c.dueDate).isValid() ? moment(c.dueDate).format('DD-MM-YYYY') : '-',
          invoiceDate: moment(c.date).isValid() ? moment(c.date).format('DD-MM-YYYY') : '-',
          amountDue: c.billStatus === 'Partly Paid' ? amountDue : '-',
          amountDueNum: c.amountDue,
          amountPaid: typeof c.amountPaid !== 'undefined' ? `${new Intl.NumberFormat('no-NO', { style: 'currency', currency: 'NOK' }).format(c.amountPaid)}` : 0,
          totalGross: typeof c.totalGross !== 'undefined' ? `${new Intl.NumberFormat('no-NO', { style: 'currency', currency: 'NOK' }).format(c.totalGross)}` : '',
          overdue,
        };
      });
    },

    billsList() {
      return this.bills.items.map((b) => {
        let cardClass = '';
        const overdue = billIsOverdueForDisplay(b);

        if (b.billStatus === 'Overdue' || overdue) {
          cardClass = 'card-red';
        } else if (b.billStatus === 'Cleared') {
          cardClass = 'card-green';
        } else if (b.billStatus === 'Sent') {
          cardClass = 'card-yellow';
        } else if (b.billStatus === 'Cancelled') {
          cardClass = 'card-green';
        }

        Object.assign(b, {
          number: b.billingNumber ? `#${b.billingNumber}` : 'Draft',
          value: `${b.totalValue.toFixed(2)} NOK`,
          amount: `(${b.totalQuantity} ${this.$t('static.bills.index.quantities')})`,
          dueDateFormated: moment(b.dueDate).isValid() ? moment(b.dueDate).format('DD-MM-YYYY') : '-',
          invoiceDateFormated: moment(b.date).isValid() ? moment(b.date).format('DD-MM-YYYY') : '-',
          cardTotalGross: typeof b.totalGross !== 'undefined' ? `${new Intl.NumberFormat('no-NO', { style: 'currency', currency: 'NOK' }).format(b.totalGross)}` : '',
          cardAmountDue: typeof b.amountDue !== 'undefined' ? `${new Intl.NumberFormat('no-NO', { style: 'currency', currency: 'NOK' }).format(b.amountDue)}` : '',
          cardClass,
          overdue,
        });

        return b;
      });
    },

    selectedCount() {
      return this.multipleSelection.length || 0;
    },
  },

  watch: {
    /**
     * Listem to router changes, used to check query params that have changed to apply filters.
     *
     * @param  {Object} route Current route object.
     */
    $route() {
      this.all(this.searchQuery.page, this.limit);
    },

    multipleSelection(multipleSelection) {
      this.showAction = false;
      if (multipleSelection.length) {
        this.showAction = true;
      }
    },

    view(view) {
      if (view == 'card') {
        this.info = 'info';
        this.primary = 'primary';
      } else {
        this.primary = 'info';
        this.info = 'primary';
      }
    },
    loading(loading) {
      if (loading) {
        this.disableInnerSidebar();
      } else {
        this.enableInnerSidebar();
      }
    },
  },

  components: {
    VCard: require('./../../../../components/card/card.vue'),
    VPagination: require('./../../../../components/paginationNew/paginationNew.vue'),
    VNoData: require('./../../../../components/noData/noData.vue'),
    VTable: require('./../../../../components/table/table.vue'),
  },

  mixins: [
    SearchMixin,
  ],

  mounted() {
    accountService.rememberPerPage('get', { from: 'bill' })
      .then((data) => {
        this.limit = data.size;
        this.all(this.searchQuery.page, this.limit);
      }).catch((error) => {
        console.log(error);
      });

    this.setInnerSidebar([
      {
        title: this.$t('static.bills.filters.title'),
        options: [
          {
            title: this.$t('static.bills.filters.today'),
            route: {
              name: 'bills.index',
              query: {
                filter: 'today',
              },
            },
          },
          /* {
            title: this.$t('static.bills.filters.upcoming'),
            route: {
              name: 'bills.index',
              query: {
                filter: 'upcoming',
              },
            },
          },*/
          {
            title: this.$t('static.bills.filters.pending'),
            route: {
              name: 'bills.index',
              query: {
                filter: 'pending',
              },
            },
          },
          {
            title: this.$t('static.bills.filters.sent'),
            route: {
              name: 'bills.index',
              query: {
                filter: 'sent',
              },
            },
          },
          {
            title: this.$t('static.bills.filters.overdue'),
            route: {
              name: 'bills.index',
              query: {
                filter: 'overdue',
              },
            },
          },
          {
            title: this.$t('static.bills.filters.cancelled'),
            route: {
              name: 'bills.index',
              query: {
                filter: 'cancelled',
              },
            },
          },
          {
            title: this.$t('static.bills.filters.paid'),
            route: {
              name: 'bills.index',
              query: {
                filter: 'paid',
              },
            },
          },
          {
            title: this.$t('static.bills.filters.partlyPaid'),
            route: {
              name: 'bills.index',
              query: {
                filter: 'partlyPaid',
              },
            },
          },
          {
            title: this.$t('static.bills.filters.creditNote'),
            route: {
              name: 'bills.index',
              query: {
                filter: 'credit-note',
              },
            },
          },
          {
            title: this.$t('static.bills.filters.invoice'),
            route: {
              name: 'bills.index',
              query: {
                filter: 'invoice',
              },
            },
          },
          {
            title: this.$t('static.bills.filters.all'),
            route: {
              name: 'bills.index',
            },
          },
        ],
        button: {
          title: `+ ${this.$t('static.bills.create.title')}`,
          route: {
            name: 'bills.create',
          },
        },

      },
      {
        title: this.$t('layouts.sidebar.billExport'),
        button: {
          title: this.$t('static.bills.exportBill.title'),
          route: {
            name: 'bills.export',
          },
        },
      },
    ]);

    if (this.view == 'card') {
      this.info = 'info';
      this.primary = 'primary';
    } else {
      this.primary = 'info';
      this.info = 'primary';
    }
  },

  destroyed() {
    this.unsetInnerSidebar();
  },

  methods: {
    ...mapMutations({
      setInnerSidebar: 'SET_INNERSIDEBAR',
      unsetInnerSidebar: 'UNSET_INNERSIDEBAR',
      disableInnerSidebar: 'DISABLE_INNERSIDEBAR',
      enableInnerSidebar: 'ENABLE_INNERSIDEBAR',
      toggleModal: 'TOGGLE_MODAL',
      setModalContent: 'SET_MODAL_CONTENT',
      setModalProps: 'SET_MODAL_PROPS',
      unsetModalContent: 'UNSET_MODAL_CONTENT',
      setModalCloseOnClick: 'SET_MODAL_CLOSE_ON_CLICK',
      setModalShowClose: 'SET_MODAL_SHOW_CLOSE',
      setModalCloseOnPressEscape: 'SET_MODAL_CLOSE_ON_PRESS_ESCAPE',
    }),

    handleSelectionChange(val) {
      this.multipleSelection = val;
    },

    onSelectAction(value) {
      if (this.multipleSelection.length > this.bulkSelectionLimit) {
        const message = `${this.$t('static.bills.index.bulkMaxLimitExceededError')} ${this.bulkSelectionLimit}`;
        this.$message({
          showClose: true,
          message,
          customClass: 'error',
        });
        return;
      }

      if (value === 'downloadInvoicePdf') {
        invoiceService.bulkDownload(this.multipleSelection.map(item => item.hashid));
        return;
      }
      if (value) {
        this.setModalContent(() => bulkProcessBillModal);
        this.setModalProps({
          selectedBills: this.multipleSelection,
          operation: value,
          onSubmit: this.handleBulkBillActionSubmit,
        });
        this.setModalCloseOnClick(false);
        this.setModalShowClose(false);
        this.setModalCloseOnPressEscape(false);
        this.toggleModal(true);
      }
    },

    handleBulkBillActionSubmit() {
      this.multipleSelection = [];
      this.all(1, 25);
    },

    perPage(size) {
      accountService.rememberPerPage('post', { from: 'bill', perPage: size })
        .then((data) => {
          this.limit = data.size;
        }).catch((error) => {
          console.log(error);
        });
    },

    changeView(view) {
      if (this.info != view) {
        this.primary = this.info;
        this.info = view;
        if (this.info == 'info') {
          store.dispatch('setView', 'card');
        } else {
          store.dispatch('setView', 'list');
        }
      }
    },
    onChangeSearch(name, value) {
      store.dispatch('setBillQuery', { page: 1, limit: this.limit, search: value });
      this.all(1, this.limit);
    },

    /**
     * Fetches paginated Bills.
     *
     * @param  {int} page The page number.
     * @param  {int} limit The number of items displayed on a page.
     */
    all: debounce(function (page, limit) {
      this.loading = true;
      const query = { page, limit, search: this.searchQuery.search };
      store.dispatch('setBillQuery', query);
      this.search = this.searchQuery.search;
      billService.items({
        page,
        limit,
        search: this.searchQuery.search,
        filter: this.$route.query.filter,
      }).then(() => {
        this.loading = false;
      }).catch(() => {
        this.loadProductsing = false;
      });
    }, 800),

    /**
     * Go to edit view for bill.
     *
     * @param  {Object} bill The bill object.
     */
    editBill(bill) {
      const name = (bill.billType !== 'Credit Note') ? 'bills.edit' : 'bills.creditNote';
      this.$router.push({
        name,
        params: { id: bill.id },
      });
    },
    /**
     * Go to edit view for bill on new tab.
     *
     * @param  {Object} bill The bill object.
     */
    editBillNewTab(bill) {
      const name = (bill.billType !== 'Credit Note') ? 'bills.edit' : 'bills.creditNote';
      const route = this.$router.resolve({
        name,
        params: { id: bill.id },
      });
      window.open(route.href, '_blank');
    },

    billRowClassName({ row }) {
      return row.overdue ? 'bill-row-overdue' : '';
    },
  },
};



// WEBPACK FOOTER //
// ./src/app/pages/withNavigation/bills/index/index.js